import React, { useState, useMemo } from "react";
import ReactECharts from "echarts-for-react";
import NightingaleChart from "./NightingaleChart";
import { useTheme } from "../contexts/ThemeContext";

export const Card = ({ children, className = "" }) => (
    <div className={` shadow-md rounded-2xl p-4 ${className}`}>
        {children}
    </div>
);

export const CardContent = ({ children, className = "" }) => (
    <div className={`p-2 ${className}`}>
        {children}
    </div>
);

const MMMInsights = ({ insights }) => {

    const { theme } = useTheme();
    const isDark = theme === 'dark';
    const axisColor = isDark ? '#ccc' : '#333';

    const [selectedChannel, setSelectedChannel] = useState(null);

    const handleChartClick = (params) => {
        const name = params.name;
        setSelectedChannel(prev => (prev === name ? null : name)); // toggle
    };

    const newArray = [...insights.cpm_analysis.data].sort((a, b) => a.cpm - b.cpm);
    const cpmData = newArray.slice(0, -2);

    const cpcArray = [...insights.cpc_analysis.data].sort((a, b) => a.cpc - b.cpc);
    const cpcData = cpcArray.slice(0, -2);

    const impressionData = insights.top_impression_channels.channels.map(d => ({
        name: d.channel,
        value: d.total_impressions_millions
    }));

    const clickData = insights.top_click_channels.channels.map(d => ({
        name: d.channel,
        value: d.total_clicks_millions
    }));

    const revenueData = insights.seasonal_trends.monthly_revenue;
    const holidays = insights.holiday_impact.analysis;

    const getImpactColor = (impact) => {
        if (impact > 0) return "text-green-600";
        if (impact < 0) return "text-red-600";
        return "text-gray-600";
    };

    const filteredCPM = useMemo(
        () => selectedChannel ? cpmData.filter(d => d.channel === selectedChannel) : cpmData,
        [selectedChannel, cpmData]
    );

    const filteredCPC = useMemo(
        () => selectedChannel ? cpcData.filter(d => d.channel === selectedChannel) : cpcData,
        [selectedChannel, cpcData]
    );

    const filteredImpressions = useMemo(
        () => selectedChannel ? impressionData.filter(d => d.name === selectedChannel) : impressionData,
        [selectedChannel, impressionData]
    );

    const filteredClicks = useMemo(
        () => selectedChannel ? clickData.filter(d => d.name === selectedChannel) : clickData,
        [selectedChannel, clickData]
    );

    return (
        <div className="p-6 grid grid-cols-1 sm:grid-cols-2 gap-6">
            <Card className="sm:col-span-2">
                <CardContent>
                    <h3 className="text-lg font-semibold mb-4">Holiday Impact on Revenue</h3>
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                        {Object.entries(holidays).map(([holiday, data]) => (
                            <div key={holiday} className="bg-gray-50 dark:bg-gray-800 p-4 rounded-xl shadow-sm">
                                <h4 className="text-md font-semibold">{data.holiday}</h4>
                                <p className={`text-xl font-bold ${getImpactColor(data.impact_percentage)}`}>
                                    {data.impact_percentage.toFixed(2)}%
                                </p>
                                <p className="text-sm text-gray-600 dark:text-gray-400">
                                    Holiday Avg: ${data.holiday_avg_revenue.toLocaleString()}
                                </p>
                                <p className="text-sm text-gray-600 dark:text-gray-400">
                                    Non-Holiday Avg: ${data.non_holiday_avg_revenue.toLocaleString()}
                                </p>
                            </div>
                        ))}
                    </div>
                </CardContent>
            </Card>

            {/* Filter Info */}
            {selectedChannel && (
                <div className="sm:col-span-2 text-blue-600 text-sm mb-[-12px] cursor-pointer" onClick={() => setSelectedChannel(null)}>
                    Filtered by: <b>{selectedChannel}</b> (click to clear)
                </div>
            )}

            {/* CPM */}
            <Card className="sm:col-span-2">
                <CardContent>
                    <h3 className="text-lg font-semibold mb-2">Cost Per Mille (Thousand)</h3>
                    <ReactECharts
                        style={{ height: 500, width: "100%" }}
                        option={{
                            tooltip: { trigger: "axis" },
                            legend: { data: ["CPM"], textStyle: { color: axisColor }, },
                            grid: { bottom: 100 },
                            xAxis: {
                                type: "category",
                                data: filteredCPM.map(d => d.channel),
                                axisLabel: { rotate: 45, color: axisColor }
                            },
                            yAxis: { type: "value", name: "CPM ($)", axisLabel: { color: axisColor } },
                            series: [{
                                name: "CPM",
                                type: "bar",
                                data: filteredCPM.map(d => d.cpm),
                                itemStyle: { color: "#8884d8" }
                            }]
                        }}
                        onEvents={{ click: handleChartClick }}
                    />
                </CardContent>
            </Card>

            {/* CPC */}
            <Card className="sm:col-span-2">
                <CardContent>
                    <h3 className="text-lg font-semibold mb-2">Cost Per Click</h3>
                    <ReactECharts
                        style={{ height: 500, width: "100%" }}
                        option={{
                            tooltip: { trigger: "axis" },
                            legend: { data: ["CPC"], textStyle: { color: axisColor }, },
                            grid: { bottom: 100 },
                            xAxis: {
                                type: "category",
                                data: filteredCPC.map(d => d.channel),
                                axisLabel: { rotate: 45, color: axisColor }
                            },
                            yAxis: { type: "value", name: "CPC ($)", axisLabel: { color: axisColor } },
                            series: [{
                                name: "CPC",
                                type: "bar",
                                data: filteredCPC.map(d => d.cpc),
                                itemStyle: { color: "#8884d8" }
                            }]
                        }}
                        onEvents={{ click: handleChartClick }}
                    />
                </CardContent>
            </Card>

            {/* Impressions */}
            <Card>
                <CardContent>
                    <h3 className="text-lg font-semibold mb-2">Top Channels by Impressions</h3>
                    <span>(in millions)</span>
                    <ReactECharts
                        style={{ height: 350, width: "100%" }}
                        option={{
                            tooltip: { trigger: "axis" },
                            grid: { bottom: 80 },
                            xAxis: {
                                type: "category",
                                data: filteredImpressions.map(d => d.name),
                                axisLabel: { rotate: 45, color: axisColor }
                            },
                            yAxis: { type: "value", axisLabel: { color: axisColor } },
                            series: [{
                                name: "Impressions",
                                type: "bar",
                                data: filteredImpressions.map(d => d.value),
                                itemStyle: { color: "#8884d8" }
                            }]
                        }}
                        onEvents={{ click: handleChartClick }}
                    />
                </CardContent>
            </Card>

            {/* Clicks */}
            <Card>
                <CardContent>
                    <h3 className="text-lg font-semibold mb-2">Top Channels by Clicks</h3>
                    <span>(in millions)</span>
                    <ReactECharts
                        style={{ height: 350, width: "100%" }}
                        option={{
                            tooltip: { trigger: "axis" },
                            grid: { bottom: 80 },
                            xAxis: {
                                type: "category",
                                data: filteredClicks.map(d => d.name),
                                axisLabel: { rotate: 45, color: axisColor }
                            },
                            yAxis: { type: "value", axisLabel: { color: axisColor } },
                            series: [{
                                name: "Clicks",
                                type: "bar",
                                data: filteredClicks.map(d => d.value),
                                itemStyle: { color: "#82ca9d" }
                            }]
                        }}
                        onEvents={{ click: handleChartClick }}
                    />
                </CardContent>
            </Card>

            {/* Revenue Line Chart (Unfiltered) */}
            <Card className="sm:col-span-2">
                <CardContent>
                    <h3 className="text-lg font-semibold mb-2">Monthly Revenue Trends</h3>
                    <ReactECharts
                        style={{ height: 300, width: "100%" }}
                        option={{
                            tooltip: {
                                trigger: "axis",
                                formatter: params => {
                                    const p = params[0];
                                    return `${p.axisValue}<br/>Revenue: $${p.data.formatted}`;
                                }
                            },
                            xAxis: {
                                type: "category",
                                data: revenueData.map(d => d.month),
                                axisLabel: { color: axisColor }
                            },
                            yAxis: { type: "value", name: "Revenue ($)", axisLabel: { color: axisColor } },
                            series: [{
                                name: "Revenue",
                                type: "line",
                                smooth: true,
                                data: revenueData.map(d => ({
                                    value: d.revenue,
                                    formatted: d.formatted_revenue
                                })),
                                itemStyle: { color: "#ff8042" },
                                lineStyle: { width: 2 }
                            }]
                        }}
                    />
                </CardContent>
            </Card>

            {/* Economic Indicators */}
            <Card className="sm:col-span-2">
                <CardContent>
                    <h3 className="text-lg font-semibold mb-2">Correlation between economic indicators and revenue</h3>
                    <NightingaleChart indicators={insights.economic_correlation.indicators} />
                </CardContent>
            </Card>
        </div>
    );
};

export default MMMInsights;
